<template>
  <app-page :title="$t('shifts.title')" :subtitle="$t('shifts.subtitle')" icon="calendar-alt">
    <template v-slot:after-header>
      <shift-status-alerts showShiftUploadDetail="true" class="mt-3" />
    </template>

    <template v-slot:infobox>
      <sme-info-box :title="$t('shifts.infoBox.aboutTitle')" id="about-shifts" save-state>
        <p>{{ $t('shifts.infoBox.aboutIntro') }}</p>
        <ul>
          <li>{{ $t('shifts.infoBox.aboutSalariedBullet') }}</li>
          <li>{{ $t('shifts.infoBox.aboutShiftWorkerBullet') }}</li>
        </ul>
        <p>
          <strong>{{ $t('shifts.infoBox.aboutExperienceBold') }}</strong>
        </p>
        <p>{{ $t('shifts.infoBox.aboutDeleteBody', { binIcon: bankingInfo.binIcon }) }}</p>
        <ul>
          <li>{{ $t('shifts.infoBox.aboutDeleteRotaBullet', { rotaProvider: bankingInfo.rota.rotaProvider }) }}</li>
          <li>
            {{
              $t('shifts.infoBox.aboutDeletePayPeriodBullet', {
                substantive: bankingInfo.stream.substantive.toLowerCase() + 's',
              })
            }}
          </li>
        </ul>
      </sme-info-box>

      <sme-info-box :title="$t('shifts.infoBox.addTitle')" id="about-worked-shifts" save-state>
        <p>
          <i18n path="shifts.infoBox.addIntegrationBody" tag="span">
            <template #link
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('common.integrationsLinkText')
              }}</router-link></template
            >
            <template #rotaLabel>{{ bankingInfo.rota.label }}</template>
          </i18n>
        </p>
        <p>
          <i18n path="shifts.infoBox.addManualBody" tag="span">
            <template #individualLink
              ><router-link :to="{ name: 'sme-shift-add' }">{{
                $t('shifts.infoBox.individuallyLinkText')
              }}</router-link></template
            >
            <template #bulkLink
              ><router-link :to="{ name: 'sme-shifts-upload' }">{{
                $t('common.inBulkLinkText')
              }}</router-link></template
            >
          </i18n>
        </p>
      </sme-info-box>
      <sme-info-box :title="$t('shifts.infoBox.editTitle')" id="editing-shifts" save-state>
        <p>{{ $t('shifts.infoBox.editBody1') }}</p>
        <p>{{ $t('shifts.infoBox.editBody2', { rotaProvider: bankingInfo.rota.rotaProvider }) }}</p>
      </sme-info-box>
    </template>
    <template v-slot:header v-if="showShiftUploadSection">
      <b-button
        v-if="canAddShifts && canAddSingleShift"
        :to="{ name: 'sme-shift-add' }"
        variant="outline-primary"
        :disabled="!State.state.paySchedule"
      >
        <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />{{ $t('shifts.addShiftButton') }}
      </b-button>
      <b-button
        v-if="canAddShifts"
        :to="{ name: 'sme-shifts-upload' }"
        variant="outline-primary"
        :disabled="!State.state.paySchedule"
      >
        <font-awesome-icon :icon="['fal', 'upload']" class="mr-2" /> {{ $t('common.bulkImportButton') }}
      </b-button>
      <b-button
        v-if="canIntegrate && IntegrationsState.state.loaded && !IntegrationsState.state.integrations"
        :to="{ name: 'sme-integrations' }"
        variant="outline-primary"
      >
        <font-awesome-icon :icon="['fad', 'chart-network']" class="mr-2" />{{ $t('common.setupIntegrationButton') }}
      </b-button>
    </template>

    <template v-slot:before-content>
      <b-col sm="12" class="mb-4">
        <shift-stats />
      </b-col>
    </template>

    <template v-slot:custom-content>
      <b-col>
        <b-card>
          <shifts-list v-if="!displayNetPayShifts" class="mt-3" />
          <net-pay-shifts-list v-if="displayNetPayShifts" class="mt-3" />
        </b-card>
      </b-col>
    </template>
  </app-page>
</template>

<script setup>
import { computed } from 'vue';
import { getBankingInfo } from '@/Banking';
import AppPage from '@/components/AppPage.vue';
import ShiftStatusAlerts from '@/components/ShiftStatusAlerts.vue';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import ShiftStats from '@/pages/shifts/components/ShiftStats.vue';
import ShiftsList from '@/pages/shifts/components/ShiftsList.vue';
import NetPayShiftsList from '@/pages/shifts/components/NetPayShiftsList.vue';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';

const { canAddShifts, canAddSingleShift, canIntegrate, displayNetPayShifts } = useFeatureFlags();
// Allow shift uploads if user is an admin, or they are a manager and the company allows it
const showShiftUploadSection =
  State.state.claims.a ||
  (State.state.claims.m && State.state.company.properties.portal?.enable_shift_uploads_from_managers);
const bankingInfo = computed(() => getBankingInfo());
</script>
